// @ts-strict-ignore
import { useEffect, useRef } from 'react';

import cx from 'classnames';

import Divider from 'components/divider';
import { Text } from 'components/typography';
import { trackOutboundLinks } from 'lib/analytics/mixpanel-helpers';

import { LearnModuleTypes } from 'api';
import Markdown from 'components/markdown';
import useVideoPlayerStore from 'components/video-player-dubbing/store';
import type LearnModuleCardProps from './LearnModuleCard.props';

const LearnModuleCard = ({
  blurb,
  eyebrow,
  linkUrl,
  headline,
  moduleImageUrl,
  showDivider = true,
  author,
  publisher,
  year,
  type
}: LearnModuleCardProps) => {
  const linkRef = useRef(null);
  const autoplayed = useVideoPlayerStore(store => store.autoplayed);

  useEffect(() => {
    trackOutboundLinks(
      linkRef.current,
      'learning module cta',
      linkUrl,
      autoplayed
    );
  }, [headline, linkUrl, autoplayed]);

  return (
    <>
      <a
        ref={linkRef}
        href={linkUrl}
        target="_blank"
        rel="noreferrer noopener"
        className={cx('group flex flex-col items-start md:flex-row', {
          'mb-6': showDivider
        })}
      >
        <div className="flex flex-col justify-between md:w-3/4 md:pr-8">
          {eyebrow && (
            <Text size="s" className="mb-3 text-gray-500">
              {eyebrow}
            </Text>
          )}
          <div className="mb-4">
            <Text size="l" className="mb-6 font-bold group-hover:underline">
              <Markdown>{headline}</Markdown>
            </Text>

            {type === LearnModuleTypes.Book && (
              <span>
                {author} |{' '}
                <Text size="s" className="mb-2 text-gray-500" tagName="span">
                  {publisher} ({year})
                </Text>
              </span>
            )}

            <Text size="m">
              <Markdown>{blurb}</Markdown>
            </Text>
          </div>
        </div>
        <div className="md:w-1/4">
          {moduleImageUrl && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              className="mx-auto block h-auto max-h-44"
              src={moduleImageUrl}
              alt={`${author} - ${headline}`}
            />
          )}
        </div>
      </a>
      {showDivider && <Divider className="mb-4" />}
    </>
  );
};

export default LearnModuleCard;
